import { IConferenceDeprecatedService } from '@agent/core/phone/conference/conference-deprecated.service';
import { GuestState, GuestType } from '@agent/core/phone/conference/models/guest';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NG1_CONFERENCE } from '@commons/core/core.provider.ajs';
import { TOptions } from 'i18next';
import { combineLatest, merge, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';

import { BookingFacade, BookingStatus, isNotNullOrUndefined, MediaType, ProfileFacade, TranslateService, UserUtil } from '@contact/upgrade';

import { AgentDesktopRouter } from './agent-desktop-router';
import { AgentDesktopState } from './agent-desktop-routing.module';
import { AgentBookingChatInprocessState } from './contact-view/chat/state/agent-booking-chat-routing.module';
import { AgentBookinMailInprocessState } from './contact-view/mail/state/agent-booking-mail-routing.module';
import { AgentBookinPhoneInprocessState } from './contact-view/phone/state/agent-booking-phone-routing.module';
import { AgentBookinSocialNetworkInprocessState } from './contact-view/social-network/state/agent-booking-social-network-routing.module';

@Injectable({
  providedIn: 'root',
})
export class AgentTitleService {
  private readonly url$ = this.router.currentUrl$;
  private readonly contact$ = this.contactFacade.selected$;
  private readonly customer$ = this.contactFacade.selectedCustomer$;
  private readonly language$ = this.profileFacade.language$;

  private readonly _destroy$ = new Subject<void>();
  readonly destroy$ = this._destroy$.asObservable();

  private _url = '';

  constructor(
    private readonly title: Title,
    private readonly profileFacade: ProfileFacade,
    private readonly translateService: TranslateService,
    private readonly contactFacade: BookingFacade,
    private readonly router: AgentDesktopRouter,
    @Inject(NG1_CONFERENCE) private readonly conferenceDeprecatedService: IConferenceDeprecatedService
  ) {}

  init() {
    this.url$.pipe(isNotNullOrUndefined(), takeUntil(this.destroy$)).subscribe(url => (this._url = url));
    merge(this.language$, this.url$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh());
  }

  destroy() {
    this._destroy$.next();
    this.title.setTitle(this.translate('agent-desktop-contact'));
  }

  refresh(): void {
    this.getTitle()
      .pipe(takeUntil(this.destroy$))
      .subscribe(title => this.title.setTitle(title));
  }

  private getTitle(): Observable<string> {
    switch (true) {
      case this.isActiveForState([AgentDesktopState.ACTIVITY_MONITORING]):
        return this.translateObs('agent-desktop-view-activity-monitoring');
      case this.isActiveForState([AgentDesktopState.BASKET]):
        return this.translateObs('agent-desktop-basket');
      case this.isActiveForState([AgentDesktopState.BOOKING]):
        return this.getBookingTitle();
      case this.isActiveForState([AgentDesktopState.CENTER]):
        return this.translateObs('agent-desktop-center');
      case this.isActiveForState([AgentDesktopState.QUEUES]):
        return this.translateObs('agent-desktop-queues');
      case this.isActiveForState([AgentDesktopState.INDICATORS]):
        return this.translateObs('agent-desktop-view-indicators');
      case this.isActiveForState([AgentDesktopState.INFOS]):
        return this.translateObs('agent-desktop-view-information');
      case this.isActiveForState([AgentDesktopState.LAST_EXCHANGES]):
        return this.translateObs('agent-desktop-view-exchanges');
      case this.isActiveForState([AgentDesktopState.RECORDS]):
        return this.translateObs('agent-desktop-view-records');
      default:
        return this.translateObs('agent-desktop-contact');
    }
  }

  private getBookingTitle(): Observable<string> {
    return combineLatest({
      booking: this.contact$,
      customer: this.customer$,
    }).pipe(
      take(1),
      switchMap(({ booking, customer }) => {
        const guests = (booking && this.conferenceDeprecatedService.getGuests(booking.responseId)) ?? [];
        const targetParked = guests.some(({ type, state }) => type === GuestType.TARGET && state === GuestState.PARKED);
        const contactParked = guests.some(({ type, state }) => type === GuestType.CONTACT && state === GuestState.PARKED);
        const name = (customer && UserUtil.fullname(customer)) ?? '';
        switch (true) {
          case targetParked:
            return this.translateObs('agent-desktop-contact-phone-conference-agent-parked', { name });
          case contactParked:
            return this.translateObs('agent-desktop-contact-phone-conference-contact-parked', { name });
          default:
            return this.translateObs(this.getBookingKey(), { name });
        }
      })
    );
  }

  private getBookingKey(): string {
    switch (true) {
      /**
       * Phone
       */
      case this.isActiveForState([MediaType.PHONE.toLowerCase(), BookingStatus.ALLOCATED.toLowerCase()]):
        return 'agent-desktop-contact-phone-allocated';
      case this.isActiveForState([MediaType.PHONE.toLowerCase(), BookingStatus.ACCEPTING.toLowerCase()]):
        return 'agent-desktop-contact-phone-accepting';
      case this.isActiveForState([
        MediaType.PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.call,
      ]):
        return 'agent-desktop-contact-phone-in-process';
      case this.isActiveForState([
        MediaType.PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.conference,
      ]):
        return 'agent-desktop-contact-phone-conference';
      case this.isActiveForState([
        MediaType.PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.forward,
      ]):
        return 'agent-desktop-contact-phone-transfer';
      case this.isActiveForState([
        MediaType.PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.scriptRedirection,
      ]):
        return 'agent-desktop-contact-phone-in-process';
      case this.isActiveForState([MediaType.PHONE.toLowerCase(), BookingStatus.POST_PROCESSING.toLowerCase()]):
        return 'agent-desktop-contact-phone-post-treatment';

      /**
       * Phone unit
       */
      case this.isActiveForState([MediaType.UNITARY_PHONE.toLowerCase(), BookingStatus.ALLOCATED.toLowerCase()]):
        return 'agent-desktop-contact-phone-allocated';
      case this.isActiveForState([MediaType.UNITARY_PHONE.toLowerCase(), BookingStatus.ACCEPTING.toLowerCase()]):
        return 'agent-desktop-contact-phone-accepting';
      case this.isActiveForState([
        MediaType.UNITARY_PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.call,
      ]):
        return 'agent-desktop-contact-phone-in-process';
      case this.isActiveForState([
        MediaType.UNITARY_PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.conference,
      ]):
        return 'agent-desktop-contact-phone-conference';
      case this.isActiveForState([
        MediaType.UNITARY_PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.forward,
      ]):
        return 'agent-desktop-contact-phone-transfer';
      case this.isActiveForState([
        MediaType.UNITARY_PHONE.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinPhoneInprocessState.scriptRedirection,
      ]):
        return 'agent-desktop-contact-phone-in-process';
      case this.isActiveForState([MediaType.UNITARY_PHONE.toLowerCase(), BookingStatus.POST_PROCESSING.toLowerCase()]):
        return 'agent-desktop-contact-phone-post-treatment';

      /**
       * Chat
       */

      case this.isActiveForState([
        MediaType.CHAT.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookingChatInprocessState.conference,
      ]):
        return 'agent-desktop-contact-chat-conference';
      case this.isActiveForState([
        MediaType.CHAT.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookingChatInprocessState.conversation,
      ]):
        return 'agent-desktop-contact-chat';
      case this.isActiveForState([
        MediaType.CHAT.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookingChatInprocessState.forward,
      ]):
        return 'agent-desktop-contact-chat-transfer';
      case this.isActiveForState([
        MediaType.CHAT.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookingChatInprocessState.scriptRedirection,
      ]):
        return 'agent-desktop-contact-chat';
      case this.isActiveForState([MediaType.CHAT.toLowerCase(), BookingStatus.POST_PROCESSING.toLowerCase()]):
        return 'agent-desktop-contact-chat-post-treatment';

      /**
       * Social network
       */

      case this.isActiveForState([
        MediaType.SOCIAL_NETWORK.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinSocialNetworkInprocessState.conversation,
      ]):
        return 'agent-desktop-contact-social-network';
      case this.isActiveForState([
        MediaType.SOCIAL_NETWORK.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinSocialNetworkInprocessState.forward,
      ]):
        return 'agent-desktop-contact-social-network-transfer';
      case this.isActiveForState([MediaType.SOCIAL_NETWORK.toLowerCase(), BookingStatus.POST_PROCESSING.toLowerCase()]):
        return 'agent-desktop-contact-social-network-post-treatment';

      /**
       * Mail
       */

      case this.isActiveForState([MediaType.MAIL.toLowerCase(), BookingStatus.ALLOCATED.toLowerCase()]):
        return 'agent-desktop-contact-mail-allocated';
      case this.isActiveForState([MediaType.MAIL.toLowerCase(), BookingStatus.ACCEPTING.toLowerCase()]):
        return 'agent-desktop-contact-mail-accepting';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.acceptReview,
      ]):
        return 'agent-desktop-contact-mail-in-process-approval';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.answerAskForHelp,
      ]):
        return 'agent-desktop-contact-mail-in-process-resp-askForHelp';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.askForHelp,
      ]):
        return 'agent-desktop-contact-mail-in-process-askForHelp';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.assist,
      ]):
        return 'agent-desktop-contact-mail-in-process-assist';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.close,
      ]):
        return 'agent-desktop-contact-mail-in-process-close';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.delegate,
      ]):
        return 'agent-desktop-contact-mail-in-process-delegate';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.forward,
      ]):
        return 'agent-desktop-contact-mail-in-process-transfer';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.knowledge,
      ]):
        return 'agent-desktop-contact-mail-in-process-knowledge';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.rejectReview,
      ]):
        return 'agent-desktop-contact-mail-in-process-reject-approval';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.review,
      ]):
        return 'agent-desktop-contact-mail-in-process-approval-review';
      case this.isActiveForState([
        MediaType.MAIL.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.send,
      ]):
        return 'agent-desktop-contact-mail-in-process';
      case this.isActiveForState([MediaType.MAIL.toLowerCase(), BookingStatus.POST_PROCESSING.toLowerCase()]):
        return 'agent-desktop-contact-mail-post-treatment';

      /**
       * Mail unit
       */

      case this.isActiveForState(['unit_mail', BookingStatus.IN_PROCESS.toLowerCase(), AgentBookinMailInprocessState.send]):
        return 'agent-desktop-contact-mail-out-process';
      case this.isActiveForState([
        'unit_mail'.toLowerCase(),
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinMailInprocessState.knowledge,
      ]):
        return 'agent-desktop-contact-mail-out-process-knowledge';
      case this.isActiveForState(['unit_mail', BookingStatus.POST_PROCESSING.toLowerCase()]):
        return 'agent-desktop-contact-mail-post-treatment';

      default:
        return 'agent-desktop-contact';
    }
    return '';
  }

  private translateObs(key: string | string[], options?: TOptions): Observable<string> {
    return of(this.translate(key, options));
  }

  private translate(key: string | string[], options?: TOptions): string {
    return this.translateService.instant(`agent:title.${key}`, options);
  }

  private isActiveForState(commands: string[]): boolean {
    const target = commands.reduce((r, p) => `${r}/${p}`, '');
    return this._url.indexOf(target) !== -1;
  }
}
